class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.initCustomSearchTypes();

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  initCustomSearchTypes() {
    const wrapper = this.querySelector('[data-search-types-wrapper]');
    if (!wrapper) return;

    const trigger = wrapper.querySelector('[data-search-types-trigger]');
    const panel = wrapper.querySelector('[data-search-types-panel]');
    const mode = wrapper.dataset.searchTypesMode || 'product_types';
    const isMenuMode = mode === 'menu' || mode === 'vendors';

    const select = this.querySelector('#SearchProductTypes');
    const label = wrapper.querySelector('[data-search-types-label]');
    const options = wrapper.querySelectorAll('[data-search-types-option]');

    if (!trigger || !panel) return;
    if (!isMenuMode && (!select || !label || options.length === 0)) return;

    let hoverOpenTimer = null;
    let hoverCloseTimer = null;
    const HOVER_OPEN_DELAY = 150;
    const HOVER_CLOSE_DELAY = 200;

    const openPanel = () => {
      if (hoverCloseTimer) clearTimeout(hoverCloseTimer);
      hoverCloseTimer = null;
      wrapper.classList.add('is-open');
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      if (!isMenuMode && options[0]) options[0].focus?.();
    };

    const closePanel = (fromMouseLeave = false) => {
      if (hoverOpenTimer) clearTimeout(hoverOpenTimer);
      hoverOpenTimer = null;
      wrapper.classList.remove('is-open');
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (fromMouseLeave) {
        trigger.blur?.();
      } else {
        trigger.focus?.();
      }
    };

    const selectOption = (option) => {
      if (!select || !label) return;
      const value = option.dataset.value ?? '';
      select.value = value;
      label.textContent = option.textContent.trim();
      options.forEach((opt) => {
        const selected = opt === option;
        opt.classList.toggle('is-selected', selected);
        opt.setAttribute('aria-selected', selected);
      });
      select.dispatchEvent(new Event('change', { bubbles: true }));
      closePanel();
    };

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = wrapper.classList.toggle('is-open');
      panel.hidden = !isOpen;
      trigger.setAttribute('aria-expanded', isOpen);
      if (isOpen && !isMenuMode && options[0]) options[0].focus?.();
    });

    if (!isMenuMode) {
      options.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          selectOption(option);
        });
        option.setAttribute('tabindex', '0');
      });
    }

    document.addEventListener('click', (e) => {
      if (wrapper.classList.contains('is-open') && !wrapper.contains(e.target)) closePanel(true);
    });

    wrapper.addEventListener('mouseenter', () => {
      if (hoverCloseTimer) clearTimeout(hoverCloseTimer);
      hoverCloseTimer = null;
      hoverOpenTimer = setTimeout(openPanel, HOVER_OPEN_DELAY);
    });

    wrapper.addEventListener('mouseleave', () => {
      if (hoverOpenTimer) clearTimeout(hoverOpenTimer);
      hoverOpenTimer = null;
      hoverCloseTimer = setTimeout(() => closePanel(true), HOVER_CLOSE_DELAY);
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (panel.hidden) openPanel();
      }
    });

    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePanel();
        return;
      }
      if (isMenuMode) return;
      const idx = Array.from(options).indexOf(document.activeElement);
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (document.activeElement?.dataset?.searchTypesOption !== undefined) {
          selectOption(document.activeElement);
        }
        return;
      }
      if (e.key === 'ArrowDown' && idx < options.length - 1) {
        e.preventDefault();
        options[idx + 1].focus();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (idx <= 0) trigger.focus();
        else options[idx - 1].focus();
      }
    });
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku,variants.barcode'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
